import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { AppShell, Banner, Button, IconButton, Spinner, SyncIndicator, ToastProvider, useToast, type NavItem, type SyncState } from "@vavr/ui";
import { enqueue, flush, startFlushLoop, retryTransportRefusals, onEnqueue, readPeers, refreshPeers, refreshRoster, readRoster, type DeviceRecord, type LanMessage, type OutboxEntry, type Peer, type Roster, type VavrDb } from "@vavr/client";
import { DoorLinks, lanConnected, lanPublish, lanReceived, useLan } from "./lan.ts";
import { permissionsOf, type Role } from "@vavr/core";
import { api, ApiError } from "../shared/api.ts";
import { OfflineBanner, useOffline } from "../shared/OfflineBanner.tsx";
import { setLanRadio } from "../shared/radio/lan.ts";
import { applyLiveEmergency, emergencyController, startEmergency, type ServerIncident } from "../shared/emergency.ts";
import { offlineState, subscribeOffline } from "../shared/offline.ts";
import { ShellSlotsProvider, useShellSlots } from "../shared/shell-slots.tsx";
import { match, navigate, usePath } from "../shared/router.tsx";
import { setEnabledModules, slot, useTabCounts } from "../shared/slots.tsx";
import type { AppPlatform } from "../shared/platform.ts";
import { clearRestoreKey, useRestoreKey } from "../shared/restore.ts";
import { STAFF_ROLES, T, TeacherContext, type StaffSite, type TeacherCtx } from "./context.tsx";
import { TeacherSignIn, type TokenResult } from "./SignIn.tsx";
import { deviceApi, loadDevice, openTeacherDb, refreshStatus, setupPhone, wipe } from "./device.ts";
import { buildTeacherEvent, type TeacherAction } from "./events.ts";
import { useLive, type LiveMessage } from "./live.ts";
import { requestAlert } from "./pickup.ts";
import { DoorQr } from "./DoorQr.tsx";
import { Now } from "./Now.tsx";
import { ClassGrid } from "./ClassGrid.tsx";
import { Handover } from "./Handover.tsx";
import { Requests } from "./Requests.tsx";
import { Safety } from "./Safety.tsx";
import { Me } from "./Me.tsx";
import "./teacher.css";
import { UpdateRequired, useUpdateGate } from "../shared/update.tsx";

// Tabs (redesign 2026-09-27): Now gathers what needs you (waiting parents, notes and requests,
// today); Class is the photo grid with each child's sheet (restrictions included); Me is the
// phone, shift and sign-out. Modules add theirs through the "staff.tab" slot (the radio adds
// Radio after Class).
const CORE_NAV: (NavItem & { href: string })[] = [
  { id: "now", label: "Now", icon: "bell", href: T },
  { id: "class", label: "Class", icon: "child", href: `${T}/class` },
  { id: "me", label: "Me", icon: "user", href: `${T}/me` },
];

/** `may`: whether this member of staff holds a permission here; a module's tab that needs one they lack is hidden. */
export function staffNav(may: (permission: string) => boolean = () => true, counts: Record<string, number> = {}): (NavItem & { href: string })[] {
  const nav = [...CORE_NAV];
  for (const t of slot("staff.tab")) {
    if (t.permission && !may(t.permission)) continue;
    const at = t.after ? nav.findIndex((n) => n.id === t.after) + 1 : nav.length - 1;
    nav.splice(at > 0 ? at : nav.length - 1, 0, { id: t.id, label: t.label, icon: t.icon, href: `${T}/${t.path}`, count: counts[t.id] || undefined });
  }
  return nav;
}

export function teacherRoute(path: string, may: (permission: string) => boolean = () => true): { tab: string; title: string; back?: boolean; full?: boolean; topBar?: () => ReactNode; el: () => ReactNode } {
  // The roaming handover (plan §6 step 8): the kiosk HandoverFlow on this phone.
  if (match(`${T}/door`, path)) return { tab: "now", title: "Door code", el: () => <DoorQr /> };
  if (match(`${T}/handover`, path)) return { tab: "now", title: "Hand over on this phone", el: () => <Handover /> };
  if (match(`${T}/requests`, path)) return { tab: "now", title: "Today's requests", el: () => <Requests /> };
  if (match(`${T}/class`, path)) return { tab: "class", title: "My class", el: () => <ClassGrid /> };
  if (match(`${T}/safety`, path)) return { tab: "class", title: "Restrictions", el: () => <Safety /> };
  if (match(`${T}/me`, path)) return { tab: "me", title: "Me", el: () => <Me /> };
  // Modules' pages (Lost and found), under a tab, with a back arrow.
  for (const pg of slot("staff.page")) {
    if (pg.permission && !may(pg.permission)) continue;
    if (match(`${T}/${pg.path}`, path) || path.startsWith(`${T}/${pg.path}/`)) return { tab: pg.tab ?? "now", title: pg.title, back: true, full: pg.full, el: pg.render };
  }
  // A module tab owns its sub-paths too (Messages: /messages/<conversation>), which get a back arrow.
  for (const t of slot("staff.tab")) {
    if (t.permission && !may(t.permission)) continue;
    if (match(`${T}/${t.path}`, path)) return { tab: t.id, title: t.title, topBar: t.topBar, el: t.render };
    if (path.startsWith(`${T}/${t.path}/`)) return { tab: t.id, title: t.title, back: true, topBar: t.topBar, el: t.render };
  }
  return { tab: "now", title: "Now", el: () => <Now /> };
}

interface Me { account: { id: string; name: string; email: string }; memberships: { role: string; org_id: string | null; site_id: string | null }[]; sites: StaffSite[]; modules?: Record<string, string[]> }

const SITE_KEY = "pmu.teacher.site";
const store = {
  get: () => { try { return localStorage.getItem(SITE_KEY); } catch { return null; } },
  set: (v: string | null) => { try { if (v) localStorage.setItem(SITE_KEY, v); else localStorage.removeItem(SITE_KEY); } catch { /* storage blocked */ } },
};

export function staffSites(me: Me): StaffSite[] {
  return me.sites.filter((s) => me.memberships.some((m) => isStaffRole(m.role) && (m.site_id === s.id || (m.site_id === null && m.org_id === s.org_id))));
}

/** Staff, or a module's role that works in the staff app (a bus driver: transport:ride). */
const isStaffRole = (role: string) => STAFF_ROLES.has(role) || permissionsOf(role as Role).has("transport:ride");

/** Only a driver here (no core staff role): the app shows the Bus tab and Me, nothing about classes. */
export function driverOnly(me: Me, site: StaffSite) {
  const here = me.memberships.filter((m) => m.site_id === site.id || (m.site_id === null && m.org_id === site.org_id));
  return here.length > 0 && !here.some((m) => STAFF_ROLES.has(m.role));
}

export function TeacherRoot({ platform, db: dbIn }: { platform: AppPlatform; db?: VavrDb }) {
  const path = usePath();
  const tokens = platform.tokens;
  const update = useUpdateGate({ apiBase: platform.apiBase, app: "teacher", appVersion: platform.appVersion });
  const db = useMemo(() => dbIn ?? openTeacherDb(), [dbIn]);
  const [session, setSession] = useState<"loading" | "out" | "in">(() => (!tokens ? "in" : tokens.loaded ? (tokens.token ? "in" : "out") : "loading"));
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [siteId, setSiteId] = useState<string | null>(store.get);
  const [device, setDevice] = useState<DeviceRecord | null>(null);
  const [pending, setPending] = useState<OutboxEntry[]>([]);
  const [sync, setSync] = useState<SyncState>("synced");
  const [version, setVersion] = useState(0);
  const bumpTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const refresh = useCallback(() => { clearTimeout(bumpTimer.current); bumpTimer.current = setTimeout(() => setVersion((v) => v + 1), 250); }, []);

  useEffect(() => {
    if (!tokens) return;
    const sync = () => setSession(tokens.token ? "in" : "out");
    const off = tokens.subscribe(sync);
    if (!tokens.loaded) tokens.load().then(sync); else sync();
    return off;
  }, [tokens]);

  useEffect(() => {
    if (session !== "in") return;
    let live = true;
    api<Me>("GET", "/api/v1/me").then((m) => { if (live) { setMe(m); setError(null); } }, (e: ApiError) => {
      if (!live) return;
      if (e.status === 401) { tokens?.clear(); setSession("out"); } else setError(e.message);
    });
    return () => { live = false; };
  }, [session, tokens]);

  const sites = useMemo(() => (me ? staffSites(me) : []), [me]);
  const site = sites.find((s) => s.id === siteId) ?? (sites.length === 1 ? sites[0]! : null);

  const loadPending = useCallback(async () => setPending(await db.outbox.where("state").equals("pending").toArray()), [db]);

  // The live lock-screen card (worker/notify-live.ts): a tap opens the waiting parents on Now;
  // "Bring <child>" answers the request first. Held until signed in with that card's site open.
  const [liveLink, setLiveLink] = useState<URL | null>(null);
  useEffect(() => platform.onDeepLink?.((url) => { if (url.startsWith("vavr://live/")) setLiveLink(new URL(url)); }), [platform]);
  useEffect(() => {
    if (!liveLink || session !== "in" || !me) return;
    const want = liveLink.searchParams.get("site");
    if (want && site?.id !== want) {
      // Another of this person's sites: open it, and act once it is open. Not theirs: drop the link.
      if (sites.some((x) => x.id === want)) { store.set(want); setSiteId(want); } else setLiveLink(null);
      return;
    }
    if (!site) return;
    setLiveLink(null);
    const request = liveLink.searchParams.get("request");
    const go = () => navigate(T);
    if (liveLink.pathname === "/coming" && request) {
      api("POST", `/api/v1/sites/${site.id}/pickup-requests/${request}`, { status: "coming" }).catch(() => undefined).finally(go);
    } else go();
  }, [liveLink, session, me, site, sites]);

  // The device record for this site, if this phone is set up.
  useEffect(() => {
    if (!site) return;
    loadDevice(db, platform.apiBase).then((d) => setDevice(d && d.site_id === site.id ? d : null));
    loadPending();
  }, [db, site?.id, loadPending]);

  // Send the outbox on start, when back online, on return to the foreground, and every 30 s.
  useEffect(() => {
    if (!device) return;
    const dapi = deviceApi(device);
    void retryTransportRefusals(db);
    const stop = startFlushLoop(db, dapi, (r) => {
      setSync(r.offline ? "offline" : r.remaining ? "pending" : "synced");
      loadPending();
      if (r.sent) refresh();
    });
    return stop;
  }, [db, device, loadPending, refresh]);

  // Offline phase 2 (PMU-271): the school's door tablets on the Wi-Fi (DoorLinks: every door found
  // by NSD, each checked as an approved door of this school). Records and answers pass both ways;
  // this phone's own records go to them as they are saved.
  // Keyed on the ids only: the device and site records are reloaded often, and every restart of
  // this effect would drop the link.
  const deviceRef = useRef(device);
  deviceRef.current = device;
  // The name the others hear on the radio over the Wi-Fi (phase 3).
  const nameRef = useRef(me?.account.name ?? "Staff");
  nameRef.current = me?.account.name ?? "Staff";
  const lanSite = device?.status === "active" ? site?.id ?? null : null;
  const lan = useLan();
  useEffect(() => {
    const link = platform.lanLink;
    const device = deviceRef.current;
    if (!link || !device || !lanSite) return;
    const dapi = deviceApi(device);
    let peers: Peer[] = [], stopped = false;
    // The saved list at once (offline, the refresh can take a minute to fail), the fresh one after.
    // The roster mirror too: a request heard only on the Wi-Fi is shown from it (names, rooms).
    const loadPeers = () => refreshPeers(db, dapi).then((p) => { peers = p; return refreshRoster(db, dapi, device.mirrorKey); }).then(() => loadRoster()).catch(() => undefined);
    const keep = setInterval(() => { void loadPeers(); }, 10 * 60_000);
    // A request at the door while the app is in the background: a local notification (no push
    // offline), once per request, for a child this phone may see (its roster mirror).
    let roster: Roster | null = null;
    const alerted = new Set<string>();
    const loadRoster = () => readRoster(db, device.mirrorKey).then((r) => { roster = r; }, () => undefined);
    void loadRoster();
    const alertFor = (m: LanMessage) => {
      if (m.k !== "event" || !link.alert || typeof document === "undefined" || document.visibilityState !== "hidden") return;
      const p = m.ev.pre;
      if ((p.type !== "pickup_request" && p.type !== "dropoff_request") || alerted.has(p.id) || Date.now() - p.device_ts > 10 * 60_000) return;
      const child = roster?.children.find((c) => c.id === p.child_id);
      if (!child) return;
      alerted.add(p.id);
      const adult = roster?.adults.find((a) => a.id === p.adult_id);
      const who = adult ? `${adult.first} ${adult.last}` : p.claimed_name ?? "A parent";
      void link.alert(p.id, p.type === "pickup_request" && p.auth_method === "app" ? `${who} asked from the app` : `${who} is at the door`, `${p.type === "dropoff_request" ? "Dropping off" : "Picking up"} ${child.first} ${child.last}`);
    };
    const links = new DoorLinks({
      link, siteId: lanSite, deviceId: device.device_id, privateKey: device.keys.privateKey, peers: () => peers,
      onMessage: (m) => { lanReceived(m); refresh(); alertFor(m); },
      onChange: (doors) => lanConnected(doors, (m) => links.publish(m)),
      // Offline phase 3: while the school is offline the radio talks through a door's relay.
      onRadio: (r) => setLanRadio(r ? { host: r.host, port: r.port, key: r.key, id: device.device_id, name: nameRef.current } : null),
    });
    // Doors are checked against the school's device list, so the saved list is read first.
    void readPeers(db).then((p) => { if (!peers.length) peers = p; }).finally(() => { if (!stopped) links.start(); void loadPeers(); });
    const unEnqueue = onEnqueue((e) => { void lanPublish({ k: "event", ev: { pre: e.pre, device_sig: e.device_sig } }); });
    return () => { stopped = true; clearInterval(keep); unEnqueue(); links.stop(); setLanRadio(null); };
  }, [platform, db, device?.device_id, lanSite, refresh]);

  // While the internet is down the door links run in the background too (a foreground service on
  // Android), so a request at the door still reaches a teacher whose app is not open.
  const offline = useOffline();
  const keepAlive = !!(platform.lanLink?.keepAlive && lanSite && !offline.online);
  useEffect(() => {
    const k = platform.lanLink?.keepAlive;
    if (!k) return;
    void k(keepAlive).catch(() => undefined);
  }, [platform, keepAlive]);
  useEffect(() => () => { void platform.lanLink?.keepAlive?.(false).catch(() => undefined); }, [platform]);

  // Emergency mode (PMU-273): the school's emergency on this phone, and the Bluetooth mesh (standby
  // when offline, full while an incident is open).
  useEffect(() => {
    const d = deviceRef.current;
    if (!d || !lanSite || !me) return;
    const ctl = startEmergency({ db, device: d, staffId: me.account.id, mesh: platform.mesh, router: false, online: () => offlineState().online, onlineChanges: subscribeOffline, api: deviceApi(d),
      current: () => api<ServerIncident>("GET", `/api/v1/sites/${lanSite}/emergency`) });
    return () => ctl.stop();
  }, [platform, db, device?.device_id, lanSite, me?.account.id]);

  // While the office has not approved the phone yet, check every 15 s.
  useEffect(() => {
    if (!device || device.status !== "pending") return;
    const dapi = deviceApi(device);
    const check = () => refreshStatus(db, dapi).then((r) => {
      if (r === null) setDevice(null);
      else if (r.device.status !== "pending") loadDevice(db, platform.apiBase).then((d) => setDevice(d ?? null));
    }, () => undefined);
    const t = setInterval(check, 15_000);
    check();
    return () => clearInterval(t);
  }, [db, device]);

  const alerts = useRef<((m: LiveMessage) => void) | null>(null);
  // Bus crews only (no roster here): no school socket, which they may not open (QA v0.8.0).
  const busOnly = !!me && !!site && driverOnly(me, site);
  const live = useLive(session === "in" && site && !busOnly ? site.id : null, platform.apiBase, (m) => { refresh(); if (m) { applyLiveEmergency(m); alerts.current?.(m); } else emergencyController()?.resync(); });

  useEffect(() => {
    if (session !== "in" || !platform.push) return;
    platform.push.permission().then(async (p) => {
      // Asks once; staff get a push for every child handed over in their class.
      if (p === "denied") return;
      const r = await platform.push!.register("teacher");
      if (r) await api("POST", "/api/v1/push-tokens", { token: r.token, platform: r.platform, app: "teacher" });
    }).catch((e) => console.warn("push registration failed", e));
    // A push may say where it leads (`link`, under the app: "messages/<conversation>").
    return platform.push.onTap?.((data) => { if (data?.link) navigate(`${T}/${data.link}`); });
  }, [session, platform]);

  const counts = useTabCounts(slot("staff.tab"));
  useEffect(() => { if (path === "/" || path === "" || (busOnly && (path === T || path === `${T}/`))) navigate(busOnly ? `${T}/bus` : T, true); }, [path, busOnly]);

  const record = useCallback(async (a: TeacherAction) => {
    if (!device || !site || !me) throw new Error("Set up this phone first.");
    const pre = buildTeacherEvent({ siteId: site.id, deviceId: device.device_id, staffId: me.account.id, monoStart: typeof performance === "undefined" ? Date.now() : performance.timeOrigin }, a);
    await enqueue(db, device.keys.privateKey, pre, []);
    await loadPending();
    setSync("syncing");
    flush(db, deviceApi(device)).then((r) => { setSync(r.offline ? "offline" : r.remaining ? "pending" : "synced"); loadPending(); refresh(); }, () => setSync("offline"));
  }, [db, device, site, me, loadPending, refresh]);

  const setupDevice = useCallback(async () => {
    if (!site || !me) return;
    await wipe(db);
    const rec = await setupPhone(db, site.id, `${me.account.name.split(" ")[0]}'s phone`.slice(0, 80), platform.apiBase, platform.appVersion);
    setDevice(rec);
  }, [db, site, me, platform]);

  const signOut = useCallback(async () => {
    if (device) {
      const r = await flush(db, deviceApi(device)).catch(() => null);
      if (!r || r.remaining) throw new Error("Some changes are not sent yet. Connect to the internet and try again.");
    }
    await api("POST", "/api/v1/auth/logout").catch(() => undefined);
    // The phone key belongs to this person: it goes with them. Sent records stay in the outbox.
    await wipe(db);
    store.set(null);
    setDevice(null); setMe(null); setSiteId(null);
    await tokens?.clear();
    await clearRestoreKey(platform);
    setSession("out");
  }, [db, device, tokens, platform]);

  const onSession = useCallback(async (r: TokenResult) => {
    if (tokens) await tokens.save(r.token, r.expires_at);
    setSession("in");
    navigate(T, true);
  }, [tokens]);

  useRestoreKey(platform, session, "teacher_app", onSession);
  const slots = useShellSlots();
  if (update.required) return <UpdateRequired gate={update} appVersion={platform.appVersion} />;
  if (session === "loading") return <div className="pmu-center"><Spinner label="Loading" /></div>;
  if (session === "out") return <ToastProvider><TeacherSignIn passkeys={platform.passkeys} onSession={onSession} /></ToastProvider>;
  if (error) return <div className="pmu-page"><Banner tone="danger" title={error} actions={<Button size="sm" onClick={() => location.reload()}>Try again</Button>} /></div>;
  if (!me) return <div className="pmu-center"><Spinner label="Loading" /></div>;
  if (!sites.length) {
    return (
      <div className="pmu-page pmu-stack">
        <Banner tone="info" title="No class yet">Your account is not staff at any school or center. Ask the office to invite you as a teacher.</Banner>
        <Button variant="secondary" onClick={signOut}>Sign out</Button>
      </div>
    );
  }
  if (!site) {
    return (
      <div className="pmu-page pmu-stack">
        <h1 className="pmu-t-h1">Where are you working today?</h1>
        {sites.map((s) => <Button key={s.id} variant="secondary" onClick={() => { store.set(s.id); setSiteId(s.id); }} fullWidth>{s.name}</Button>)}
      </div>
    );
  }

  // Only the modules this school's organisation has (PMU-185; no list from an older server: all).
  setEnabledModules((id) => me.modules?.[site.org_id]?.includes(id) ?? true);
  // What this member of staff may do here, from their roles at this site (a teacher has no Bus tab).
  const may = (permission: string) => me.memberships.some((m) => (m.site_id === site.id || (m.site_id === null && m.org_id === site.org_id)) && permissionsOf(m.role as Role).has(permission as never));
  const r = teacherRoute(path, may);
  // A driver: the Bus tab, their crew radio (the depot channel at a bus company), their messages, and Me.
  const nav = busOnly ? staffNav(may, counts).filter((n) => n.id === "bus" || n.id === "radio" || n.id === "messages" || n.id === "me") : staffNav(may, counts);
  const ctx: TeacherCtx = {
    platform, me: me.account, site, busOnly, sites, switchSite: (id) => { store.set(id); setSiteId(id); }, db, device, pending, record, version, refresh, live,
    signOut, setupDevice,
  };
  return (
    <ToastProvider>
      <ShellSlotsProvider value={slots.value}>
      <LiveAlerts bind={alerts} tz={site.tz} />
      <TeacherContext.Provider value={ctx}>
        {slot("staff.wrap").reduceRight<ReactNode>((inner, w) => <Fragment key={w.id}>{w.wrap(inner)}</Fragment>, r.full ? <div className="pmu-teacher pmu-teacher--full">{r.el()}</div> : (
        <AppShell
          title={slots.title(r.title)}
          brand={r.back ? <IconButton icon="chevron-left" label="Back" onClick={() => history.back()} /> : undefined}
          topBar={<>{r.topBar?.()}{slots.actions}<SyncIndicator state={pending.length && sync === "synced" ? "pending" : sync} pending={pending.length} compact /></>}
          nav={nav}
          activeId={r.tab}
          onNavigate={(id: string, e: MouseEvent) => { e.preventDefault(); navigate(nav.find((x) => x.id === id)!.href); }}
          className="pmu-teacher"
        >
          <OfflineBanner lan={platform.lanLink && lanSite ? { doors: lan.doors.length } : undefined} />
          {!device && r.tab !== "me" && <Banner tone="info" title="Link this phone to record changes" actions={<Button size="sm" onClick={() => navigate(`${T}/me`)}>Set up</Button>}>{busOnly ? "You can see today's buses now." : "You can view your class now."}</Banner>}
          {device?.status === "pending" && r.tab !== "me" && <Banner tone="info" title="Waiting for the office to approve this phone">Changes are saved here and sent once it is approved.</Banner>}
          {/* Bus crews land on Bus: Now and Class (the school's board) are not theirs, not even for a moment before the redirect. */}
          {busOnly && (r.tab === "now" || r.tab === "class") ? <div className="pmu-center"><Spinner label="Loading" /></div> : r.el()}
          {slot("staff.footer").filter((f) => (!f.tabs || f.tabs.includes(r.tab)) && (!f.permission || may(f.permission))).map((f) => <Fragment key={f.id}>{f.render()}</Fragment>)}
        </AppShell>
        ))}
      </TeacherContext.Provider>
      </ShellSlotsProvider>
    </ToastProvider>
  );
}

/** A toast and a vibration for every request, drop-off and pick-up in this teacher's class. */
function LiveAlerts({ bind, tz }: { bind: { current: ((m: LiveMessage) => void) | null }; tz: string }) {
  const toast = useToast();
  useEffect(() => {
    bind.current = (m) => {
      if (m.type === "event" && (m.event?.type === "pickup_request" || m.event?.type === "dropoff_request")) {
        // A parent's request routed to this phone: the card to answer is on Now.
        toast.show({ ...requestAlert(m.event.type as "pickup_request" | "dropoff_request", m.names?.adult, m.names?.child, (m.event.pre as { auth_method?: string } | undefined)?.auth_method), tone: "info", priority: "high", timeout: 15000 });
        navigator.vibrate?.([300, 150, 300, 150, 300]);
        return;
      }
      if (m.type !== "event" || !m.event || (m.event.type !== "pickup" && m.event.type !== "dropoff")) return;
      // A release in another class during an emergency reaches every phone for the board, unnamed: no alert.
      if (!m.names?.child && (m.event.pre?.flags ?? []).includes("reunification")) return;
      const child = m.names?.child ?? "A child";
      const adult = m.names?.adult ?? "an adult";
      const at = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz }).format(m.event.server_ts);
      const flagged = (m.event.server_flags ?? []).length > 0;
      toast.show({
        title: m.event.type === "pickup" ? `${child} was picked up` : `${child} was dropped off`,
        description: `By ${adult} at ${at}${flagged ? ". Check the record: it was flagged." : "."}`,
        tone: flagged ? "warning" : "success", priority: flagged ? "high" : "low", timeout: 8000,
      });
      navigator.vibrate?.(flagged ? [200, 100, 200] : 120);
    };
    return () => { bind.current = null; };
  }, [bind, tz, toast]);
  return null;
}
